import { useState } from 'react'
import { AlertCircle, Clock } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { LatenessFilters } from './LatenessFilters'
import { LatenessTable } from './LatenessTable'
import { useLatenessReport } from '../../hooks/useReports'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import type { LatenessReportFilters } from '../../types/reports.types'

export function LatenessReport() {
  const today = new Date().toISOString().split('T')[0]
  const [filters, setFilters] = useState<LatenessReportFilters>({
    date: today,
  })
  const [shouldFetch, setShouldFetch] = useState(false)

  // Organizations not available in main branch
  const { data: departments } = useDepartmentsForSelect(null)

  const {
    data: report,
    isLoading,
    error,
    isFetching,
  } = useLatenessReport(filters, shouldFetch)

  const handleFiltersChange = (newFilters: Partial<LatenessReportFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
    // Don't auto-fetch, wait for button click
  }

  const handleGenerateReport = () => {
    setShouldFetch(true)
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <LatenessFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onGenerateReport={handleGenerateReport}
            departments={departments || []}
            isLoading={isLoading || isFetching}
          />
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки отчета. Попробуйте еще раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {shouldFetch && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Отчет по опозданиям
            </CardTitle>
            <CardDescription>
              {report ? (
                <>
                  Дата: {report.meta.date} | Найдено: {report.meta.total} сотрудников |
                  Опоздавших: {report.meta.totalLate} | Допустимое опоздание:{' '}
                  {report.meta.lateTolerance} мин
                </>
              ) : (
                'Загрузка...'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : report && report.data.length > 0 ? (
              <LatenessTable records={report.data} />
            ) : (
              <div className="text-center py-8">
                <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  Опоздавших не найдено
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Все сотрудники пришли вовремя или еще не зарегистрировали приход.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial state - before report generation */}
      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Выберите параметры и нажмите "Сформировать отчет"
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Отчет покажет всех сотрудников, опоздавших на работу в указанную дату.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
